// client/src/components/LoginScreenV2.jsx
// Redesign of LoginScreen.jsx — same brand and full story, about half the
// text. PREVIEW at /login-new while LoginScreen.jsx stays live; once
// approved this replaces it and the old file + preview route are removed.
//
// Structure (every fact kept, every description trimmed to 1–2 sentences):
//   1. Hero — floating card + headline + Google sign-in + live totals.
//   2. Features — all 10 capability tiles, one tight sentence each.
//   3. Voucher — printed-slip mock + framing + 3 loading channels.
//   4. How it works — 4 steps, two sentences each.
//   5. Roles — 4 compact role cards (who touches what).
//   6. Pricing — 3 cards with short bullets.
//   7. Contact — one line.

import { useState, useEffect, Fragment } from 'react';
import VersionFooter from './VersionFooter.jsx';
import { startGoogleLogin } from '../hooks/useMe.js';
import {
  CreditCardIcon,
  ShoppingBagIcon,
  DevicePhoneMobileIcon,
  TrophyIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  Cog6ToothIcon,
  CalendarDaysIcon,
  TableCellsIcon,
} from './Icons.jsx';

const CURRENCY = '₪';

/* All 10 capabilities from V1 (features + extras), each description cut to
   one tight sentence that keeps the concrete facts. */
const FEATURES = [
  {
    Icon: BuildingLibraryIcon, tone: 'joy', title: 'מותאם לציבור החרדי',
    line: 'בנוי לתלמודי תורה ולבתי יעקב, בלי שום צורך באינטרנט — שוברים מודפסים, מסופונים ועמדות נדרים פלוס.',
  },
  {
    Icon: CreditCardIcon, tone: 'mint', title: 'שובר על מעשה טוב',
    line: 'המורה נותן שובר נקודות מודפס על מעשה טוב, והתלמיד טוען אותו לכרטיסו בעצמו. במקום שיש מסופון — סורקים ומזכים במקום.',
  },
  {
    Icon: ShoppingBagIcon, tone: 'gold', title: 'חנות הפרסים',
    line: 'קיוסק קבוע או דוכן ליום מיוחד — התלמידים סורקים כרטיס וקונים פרסים בנקודות שצברו, בלי מזומן.',
  },
  {
    Icon: DevicePhoneMobileIcon, tone: 'brand', title: 'בירור יתרה בכל רגע',
    line: 'התלמיד בודק יתרה מתי שירצה — בעמדה בבית הספר, בעמדות נדרים פלוס ובטלפון.',
  },
  {
    Icon: TrophyIcon, tone: 'clay', title: 'טבלת מובילים',
    line: 'דירוג חי לפי יתרה, עם סינון לפי כיתה ומדליות לשלושת המקומות הראשונים.',
  },
  {
    Icon: ChartBarIcon, tone: 'sky', title: 'דוחות וייצוא לאקסל',
    line: 'כל העסקאות בקובץ אחד, סיכום הפעילות, וכמה הוצא על כל פריט בחנות.',
  },
  {
    Icon: ShieldCheckIcon, tone: 'joy', title: 'תיעוד מלא',
    line: 'כל זיכוי וחיוב נשמר עם המכשיר, פירוט הפריטים וחותמת זמן — שום עסקה לא נמחקת לתמיד.',
  },
  {
    Icon: DevicePhoneMobileIcon, tone: 'brand', title: 'עמדת שירות עצמי (קיוסק)',
    line: 'עמדת מסך מוגנת לחלוטין — בלי שום גישה לאינטרנט, מאובטחת בחומרה, בתוכנה ובסים מוגן — ואפשר לתזמן את שעות הפעילות.',
  },
  {
    Icon: CalendarDaysIcon, tone: 'mint', title: 'מודול נוכחות',
    line: 'תלמיד שמעביר כרטיס בעמדה בטווח השעות שתגדירו מזוכה אוטומטית בנקודות — פעם אחת ביום.',
  },
  {
    Icon: TableCellsIcon, tone: 'gold', title: 'טעינת נקודות מאקסל',
    line: 'זיכוי תלמידים רבים בבת אחת מקובץ אקסל — לכל תלמיד הנקודות והסיבה שלו.',
  },
];

/* Two sentences per step — the flow plus its key guarantee. */
const STEPS = [
  {
    n: 1, tone: 'brand', title: 'רושמים את הכיתות פעם אחת',
    line: 'המנהל מוסיף תלמידים ידנית או מאקסל; כל תלמיד מקבל כרטיס ושיוך לכיתה. בשנה הבאה רק מוסיפים את הכיתות החדשות.',
  },
  {
    n: 2, tone: 'mint', title: 'המורה מזכה במהלך היום',
    line: 'רואים מעשה טוב — נותנים שובר מודפס, והתלמיד טוען לבד. במקום שיש מסופון, סורקים ומזכים במקום.',
  },
  {
    n: 3, tone: 'gold', title: 'התלמיד פודה בחנות',
    line: 'בוחר פרס, סורק כרטיס, והנקודות יורדות מהיתרה. אין מספיק נקודות? המערכת לא תאשר.',
  },
  {
    n: 4, tone: 'sky', title: 'המערכת שומרת ומדווחת',
    line: 'כל עסקה נשמרת עם המכשיר שרשם אותה וחותמת זמן. בלחיצה אחת מורידים הכל לאקסל.',
  },
];

/* Who touches what — V1's role cards with the bodies cut to the essentials. */
const ROLES = [
  {
    Icon: AcademicCapIcon, tone: 'brand', title: 'התלמידים',
    summary: 'משתמשים בכרטיס, לא במערכת',
    body: 'מקבלים כרטיס פעם אחת — סורקים אצל המורה ובחנות. אין חשבון, אין סיסמא, אין אפליקציה.',
  },
  {
    Icon: CreditCardIcon, tone: 'mint', title: 'המורים',
    summary: 'מחזיקים מסופון "מורה"',
    body: 'נכנסים עם קוד בן 6 ספרות (בלי אימייל). סורקים תלמיד, בוחרים פעולה — שתי לחיצות לזיכוי.',
    device: 'מסופון EARN',
  },
  {
    Icon: BanknotesIcon, tone: 'sky', title: 'עמדת המכירה',
    summary: 'מחזיקים מסופון "חנות"',
    body: 'התלמיד סורק, המוכר מסמן את הפריטים ומאשר. אין מספיק יתרה? נחסם אוטומטית.',
    device: 'מסופון SPEND',
  },
  {
    Icon: Cog6ToothIcon, tone: 'joy', title: 'המנהל',
    summary: 'נכנס לבק־אופיס דרך Google',
    body: 'מוסיף תלמידים, יוצר מכשירים, עורך את קטלוג הפרסים והפעולות, מזמין מנהלים ומוריד דוחות.',
  },
];

const TONE_TILE = {
  mint:  'bg-mint-100  text-mint-700',
  gold:  'bg-gold-100  text-gold-700',
  brand: 'bg-brand-100 text-brand-700',
  clay:  'bg-clay-100  text-clay-700',
  sky:   'bg-sky-100   text-sky-700',
  joy:   'bg-joy-100   text-joy-700',
};
const TONE_NUM = {
  brand: 'bg-brand-gradient',
  mint:  'bg-mint-gradient',
  gold:  'bg-gold-gradient',
  sky:   'bg-sky-gradient',
};
const TONE_STRAP = {
  brand: 'bg-brand-gradient',
  mint:  'bg-mint-gradient',
  gold:  'bg-gold-gradient',
};

export default function LoginScreenV2() {
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 px-4 py-8 sm:py-12 space-y-14 sm:space-y-20">

        {/* ═════════ HERO — card, headline, sign-in, totals ═════════ */}
        <div className="space-y-6 sm:space-y-7">
          <CardShowcase />

          <h1 className="h-page text-center">
            שיהיה טוב לעשות{' '}
            <span className="bg-joy-gradient bg-clip-text text-transparent">טוב</span>
            .
          </h1>

          <section className="w-full flex justify-center animate-fade-in">
            <button
              type="button"
              onClick={() => startGoogleLogin(window.location.pathname)}
              className="inline-flex items-center justify-center gap-3 rounded-full
                         bg-white border border-ink-200 shadow-card
                         px-7 py-3.5 text-base font-bold text-ink-800
                         transition-all duration-150
                         hover:shadow-pop hover:-translate-y-0.5 hover:border-ink-300
                         active:translate-y-0 active:scale-[0.99]
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-brand-400 focus-visible:ring-offset-2"
            >
              <GoogleMark className="w-5 h-5 shrink-0" />
              כניסה עם Google
            </button>
          </section>

          <LiveStats />
        </div>

        {/* Divider between the action and the story below. */}
        <div className="flex items-center justify-center gap-3 !my-8 sm:!my-10" aria-hidden="true">
          <span className="h-0.5 w-24 sm:w-40 rounded-full bg-gradient-to-l from-transparent to-brand-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-brand-500 shadow-sm" />
          <span className="h-0.5 w-24 sm:w-40 rounded-full bg-gradient-to-r from-transparent to-brand-400" />
        </div>

        {/* ═════════ FEATURES — all 10 tiles, one sentence each ═════════ */}
        <section className="w-full max-w-6xl mx-auto !mt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FEATURES.map(({ Icon, tone, title, line }) => (
              <div key={title} className="card p-5 text-start hover:shadow-pop hover:-translate-y-0.5 hover:border-brand-200 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-2xl grid place-items-center shrink-0 ${TONE_TILE[tone]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-ink-900">{title}</h3>
                </div>
                <p className="text-sm text-ink-600 leading-relaxed">{line}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ═════════ VOUCHER — the differentiator, visual-first ═════════ */}
        <section className="w-full max-w-6xl mx-auto">
          <div className="card-pop relative overflow-hidden p-6 sm:p-10">
            <div className="absolute inset-x-0 top-0 h-2 bg-gold-gradient" />
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div className="order-2 lg:order-1">
                <MockVoucherCard />
              </div>
              <div className="order-1 lg:order-2">
                <p className="eyebrow">השובר המודפס</p>
                <h2 className="h-page mt-1.5">
                  שובר נקודות.{' '}
                  <span className="bg-gold-gradient bg-clip-text text-transparent">הפתרון המושלם.</span>
                </h2>
                <p className="mt-3 text-muted leading-relaxed">
                  לא לכל בית ספר יש תקציב למסופון אצל כל מורה. המנהל מדפיס
                  פנקסי שוברים, המורה נותן שובר על מעשה טוב, והתלמיד טוען את
                  הנקודות לכרטיסו בעצמו — באחת משלוש דרכים:
                </p>
                <ul className="mt-5 space-y-3">
                  <ChannelRow Icon={AcademicCapIcon}       tone="brand" title="עמדה בבית הספר"   detail="מעבירים כרטיס וטוענים לבד" />
                  <ChannelRow Icon={BuildingLibraryIcon}   tone="mint"  title="עמדת נדרים פלוס"  detail="בכל סניף או בית כנסת שיש בו עמדה" />
                  <ChannelRow Icon={DevicePhoneMobileIcon} tone="sky"   title="מערכת טלפונית"    detail="מכל טלפון, גם קווי — בלי אפליקציה" />
                </ul>
                <p className="mt-5 text-sm text-ink-600">
                  השובר המודפס{' '}
                  <strong className="text-ink-900">מחליף את הצורך בכל מכשיר</strong>
                  {' '}— גם בית ספר בלי אף מסופון ובלי עמדה יכול להפעיל את
                  TicTac במלואו.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ═════════ HOW IT WORKS — 4 steps, two sentences each ═════════ */}
        <section className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-7 sm:mb-9">
            <p className="eyebrow">איך זה עובד</p>
            <h2 className="h-page mt-1.5">ארבעה צעדים, ואתם בפנים.</h2>
          </div>
          <ol className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <li key={s.n} className="card p-5 hover:shadow-pop transition-shadow">
                <span
                  className={`w-9 h-9 rounded-xl text-white grid place-items-center
                             font-display font-extrabold shadow-card num mb-3 ${TONE_NUM[s.tone]}`}
                >
                  {s.n}
                </span>
                <h3 className="font-bold text-ink-900 leading-snug">{s.title}</h3>
                <p className="mt-1 text-sm text-ink-600 leading-relaxed">{s.line}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ═════════ ROLES — who touches what ═════════ */}
        <section className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-7 sm:mb-9">
            <p className="eyebrow">מי משתמש במערכת</p>
            <h2 className="h-page mt-1.5">ארבעה תפקידים, אפס בלבול.</h2>
          </div>
          <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ROLES.map(({ Icon, tone, title, summary, body, device }) => (
              <div key={title} className="card p-5 hover:shadow-pop hover:-translate-y-0.5 transition-all">
                <div className={`w-11 h-11 rounded-2xl grid place-items-center mb-3 ${TONE_TILE[tone]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-ink-900">{title}</h3>
                <p className="text-xs font-semibold text-ink-500 mt-0.5">{summary}</p>
                {device && (
                  <span className={`mt-2 inline-block ${tone === 'mint' ? 'badge-mint' : 'badge-sky'}`}>
                    {device}
                  </span>
                )}
                <p className="mt-2.5 text-sm text-ink-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ═════════ PRICING — 3 cards, short bullets ═════════ */}
        <section className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-7 sm:mb-9">
            <p className="eyebrow">מחירון</p>
            <h2 className="h-page mt-1.5">פשוט ושקוף.</h2>
          </div>
          <div className="grid gap-3 sm:gap-4 sm:grid-cols-3 items-stretch">
            <PricingCard
              tone="brand"
              eyebrow="מנוי חודשי"
              price={`${CURRENCY}2`}
              unit="לתלמיד · לחודש"
              bullets={[
                <>תקרה של <strong className="text-ink-900">{CURRENCY}300 לחודש</strong> לבית ספר</>,
                'כל הפעולות במערכת כלולות',
                'ללא הקמה, ללא התחייבות',
              ]}
              featured
            />
            <PricingCard
              tone="gold"
              eyebrow="הדפסת כרטיסים"
              price={`${CURRENCY}2`}
              unit="לכרטיס · חד פעמי"
              bullets={[
                'שם התלמיד מודפס על הכרטיס',
                'לוגו בית הספר כלול',
              ]}
            />
            <PricingCard
              tone="mint"
              eyebrow="מסופונים"
              price="לפי דרישה"
              unit="קנייה או השכרה"
              bullets={[
                'פנו לנדרים פלוס לפרטים',
                <a key="m" href="mailto:office@nedar.im" dir="ltr" className="font-bold text-brand-700 hover:text-brand-900 underline">office@nedar.im</a>,
              ]}
            />
          </div>
        </section>

        {/* ═════════ CONTACT — one line ═════════ */}
        <section className="w-full max-w-2xl mx-auto text-center">
          <p className="text-muted">
            רוצים להפעיל את TicTac בבית הספר? כתבו לנו ב־{' '}
            <a
              href="mailto:office@nedar.im"
              dir="ltr"
              className="font-bold text-brand-700 hover:text-brand-900 underline decoration-brand-300 decoration-2 underline-offset-4"
            >
              office@nedar.im
            </a>
          </p>
        </section>

      </main>
      <VersionFooter />
    </div>
  );
}

/* ───── Sub-components ───────────────────────────────────────── */

// Same outer shape on all three cards so they line up; the featured tier
// stands out via the "מומלץ" badge, not via geometry.
function PricingCard({ tone, eyebrow, price, unit, bullets, featured }) {
  return (
    <div className="card p-6 relative overflow-hidden h-full flex flex-col hover:shadow-pop hover:-translate-y-0.5 transition-all">
      <div className={`absolute inset-x-0 top-0 h-1.5 ${TONE_STRAP[tone]}`} />
      <div className="flex items-center gap-3 mb-3">
        <p className="eyebrow">{eyebrow}</p>
        {featured && <span className="badge-mint ms-auto">מומלץ</span>}
      </div>
      <div className="font-display font-extrabold text-4xl text-ink-900 num leading-none tracking-tight">
        {price}
      </div>
      <div className="mt-2 text-sm font-semibold text-ink-600">{unit}</div>
      <ul className="mt-4 pt-4 border-t border-ink-100 space-y-2 text-sm text-ink-700 flex-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckIcon className="w-4 h-4 mt-0.5 text-mint-600 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function ChannelRow({ Icon, tone, title, detail }) {
  return (
    <li className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${TONE_TILE[tone]}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0 flex flex-wrap items-baseline gap-x-2">
        <span className="font-bold text-ink-900 text-sm">{title}</span>
        <span className="text-xs text-ink-500">{detail}</span>
      </div>
    </li>
  );
}

// Live totals across every connected school (public, 1h-cached endpoint).
// Layout is reserved up front; numbers fade in once they arrive.
function LiveStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/public/stats', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setStats(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const items = [
    { value: stats?.camps, label: 'בתי ספר' },
    { value: stats?.students, label: 'תלמידים' },
    { value: stats?.pointsDistributed, label: 'נקודות חולקו' },
  ];
  return (
    <div className="flex items-center justify-center gap-x-4 sm:gap-x-6 text-xs sm:text-sm text-ink-400">
      {items.map((s, i) => (
        <Fragment key={s.label}>
          {i > 0 && <span className="text-ink-200" aria-hidden>·</span>}
          <span className="inline-flex items-baseline gap-1.5">
            <span
              className={`num font-bold text-ink-600 tabular-nums transition-opacity duration-700 ${
                stats ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {stats ? Number(s.value).toLocaleString('he-IL') : '0'}
            </span>
            <span>{s.label}</span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}

// Hero eye-candy: the real printed TicTac card as a tilted, floating 3D
// mock-up. Flips front↔back on click only. Same as V1.
function CardShowcase() {
  const [flipped, setFlipped] = useState(false);

  // Standard CR80 card ratio (85.6 × 54mm ≈ 1.586). Matches the artwork.
  const RATIO = '1.586 / 1';

  return (
    <section className="w-full max-w-md mx-auto animate-fade-in">
      <div className="relative px-4 py-6">
        <div
          className="absolute inset-8 -z-10 rounded-[44px] blur-3xl opacity-70"
          style={{ background: 'radial-gradient(60% 60% at 50% 45%, rgba(99,102,241,0.30), transparent 70%)' }}
          aria-hidden="true"
        />
        <span className="blob-drift absolute -top-2 right-4 w-24 h-24 rounded-full bg-joy-300/45 blur-2xl -z-10" aria-hidden="true" />
        <span className="blob-drift-slow absolute -bottom-2 left-4 w-28 h-28 rounded-full bg-gold-300/45 blur-2xl -z-10" aria-hidden="true" />

        <Sparkle className="twinkle text-gold-400 w-5 h-5 absolute -top-1 left-8"   style={{ animationDelay: '0s'   }} />
        <Sparkle className="twinkle text-joy-400  w-4 h-4 absolute top-7 -right-1"  style={{ animationDelay: '0.7s' }} />
        <Sparkle className="twinkle text-brand-400 w-3.5 h-3.5 absolute bottom-8 right-10" style={{ animationDelay: '1.4s' }} />
        <Sparkle className="twinkle text-mint-400 w-4 h-4 absolute -bottom-1 left-14" style={{ animationDelay: '2.1s' }} />

        <div className="card3d-float">
          <button
            type="button"
            onClick={() => setFlipped((f) => !f)}
            aria-label="הפוך את הכרטיס"
            className="card3d-scene block w-full bg-transparent border-0 p-0 cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-4 focus-visible:ring-offset-transparent"
            style={{ aspectRatio: RATIO }}
          >
            <div className={`card3d w-full h-full ${flipped ? 'is-flipped' : ''}`}>
              <div className="card3d-face shadow-pop ring-1 ring-ink-900/5">
                <img src="/card-front.jpg" alt="כרטיס TicTac — צד קדמי" className="w-full h-full object-cover" loading="eager" draggable="false" />
                <span className="card-sheen" aria-hidden="true" />
              </div>
              <div className="card3d-face card3d-back shadow-pop ring-1 ring-ink-900/5">
                <img src="/card-back.jpg" alt="כרטיס TicTac — צד אחורי" className="w-full h-full object-cover" loading="lazy" draggable="false" />
                <span className="card-sheen" aria-hidden="true" />
              </div>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}

// Four-point sparkle glyph for the decorative twinkles around the card.
function Sparkle({ className = '', style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0c.6 5.8 2.2 7.4 8 8-5.8.6-7.4 2.2-8 8-.6-5.8-2.2-7.4-8-8 5.8-.6 7.4-2.2 8-8z" />
    </svg>
  );
}

function MockVoucherCard() {
  // Mirrors src/routes/vouchers.js renderCard() — colours hard-coded to the
  // exact print hexes so the marketing preview matches the printed slip.
  return (
    <div className="relative mx-auto max-w-sm">
      <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-[28px] bg-ink-200/40 -z-10" aria-hidden="true" />
      <div className="absolute inset-0 translate-x-1 translate-y-1 rounded-[28px] bg-ink-200/60 -z-10" aria-hidden="true" />

      <div className="bg-white rounded-[28px] border border-dashed border-ink-300 shadow-card overflow-hidden">
        <div
          className="h-2"
          style={{ background: 'linear-gradient(90deg, #f43f5e 0%, #fbbf24 50%, #4f46e5 100%)' }}
          aria-hidden="true"
        />

        <div className="p-6 sm:p-7">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
              style={{ background: 'linear-gradient(135deg, #818cf8 0%, #4338ca 100%)' }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <rect x="0" y="22" width="100" height="9" fill="#312e81" opacity="0.4" />
                <ellipse cx="36" cy="55" rx="6" ry="9" fill="#fff" />
                <ellipse cx="64" cy="55" rx="6" ry="9" fill="#fff" />
                <circle cx="38" cy="58" r="3" fill="#0f172a" />
                <circle cx="66" cy="58" r="3" fill="#0f172a" />
                <ellipse cx="22" cy="74" rx="9" ry="4" fill="#fb7185" opacity="0.65" />
                <ellipse cx="78" cy="74" rx="9" ry="4" fill="#fb7185" opacity="0.65" />
                <path d="M38 76 Q50 87 62 76" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
                <path
                  d="M50 6 L53 14 L62 14 L55 19 L57 28 L50 24 L43 28 L45 19 L38 14 L47 14 Z"
                  fill="#fbbf24" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold leading-none text-lg" style={{ letterSpacing: '-0.02em' }}>
                <span style={{ color: '#f43f5e' }}>Tic</span>
                <span style={{ color: '#4f46e5' }}>Tac</span>
              </div>
              <div className="text-[11px] text-ink-500 font-semibold mt-1 truncate">בית ספר היסודי לדוגמה</div>
            </div>
          </div>

          <div className="text-center">
            <div className="text-[10px] font-bold uppercase text-ink-500" style={{ letterSpacing: '0.1em' }}>
              שובר נקודות
            </div>
            <div
              className="font-display font-extrabold leading-none mt-2 num"
              style={{ color: '#4338ca', fontSize: '56px', letterSpacing: '-0.04em' }}
            >
              <span className="opacity-85 me-1" style={{ fontSize: '32px' }}>⭐</span>10
            </div>
          </div>

          <div
            className="mt-5 rounded-xl text-center"
            style={{ border: '1.5px dashed #c7d2fe', background: '#eef2ff', padding: '10px 12px' }}
          >
            <div className="text-[10px] font-bold uppercase" style={{ color: '#4338ca', letterSpacing: '0.12em' }}>
              קוד שובר
            </div>
            <div
              className="font-display font-bold mt-1 num"
              dir="ltr"
              style={{ color: '#0f172a', fontSize: '26px', letterSpacing: '0.15em' }}
            >
              123 456 789
            </div>
          </div>

          <div className="mt-4 pt-3 flex items-center justify-between gap-2 border-t border-ink-100">
            <div className="min-w-0">
              <div className="text-[10px] text-ink-500 font-semibold leading-tight">למימוש:</div>
              <div className="font-bold text-xs mt-0.5" style={{ color: '#4338ca' }}>
                עמדת נדרים פלוס · טלפון
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Google "G" mark — inline SVG so we don't pull a logo asset for one button.
function GoogleMark({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 7.9 3l5.7-5.7A19.9 19.9 0 0 0 24 4a20 20 0 1 0 19.6 16.5z"/>
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7A19.9 19.9 0 0 0 24 4a20 20 0 0 0-17.7 10.7z"/>
      <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5.1A20 20 0 0 0 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.6l6.2 5.2c-.4.4 6.5-4.8 6.5-14.8 0-1.3-.2-2.7-.4-3.5z"/>
    </svg>
  );
}
